import { PaletteColor, Theme } from "@mui/material/styles";
import { CompanySummary, MeetingNotesSummary, PillarStatus } from "./types";

import { ALL_CATEGORIES_VALUE } from "./components/MeetingHistorySection";

export const getPillarStatusColor = (
    status: PillarStatus | undefined,
    theme: Theme
): PaletteColor => {
    switch (status) {
        case "SUCCESS":
            return theme.palette.success;
        case "WARNING":
            return theme.palette.warning;
        case "DANGER":
            return theme.palette.error;
        default:
            return theme.palette.info;
    }
};

export const getSentimentIcon = (sentiment: string): string => {
    switch (sentiment) {
        case "thumbs_up":
            return "👍";
        case "thumbs_down":
            return "👎";
        case "neutral":
            return "😐";
        default:
            console.warn("Unknown sentiment:", sentiment);
            return "🤷";
    }
};

// Helper function to get sentiment color
export const getSentimentColor = (sentiment: string, theme: Theme): string => {
    switch (sentiment) {
        case "thumbs_up":
            return theme.palette.success.main;
        case "thumbs_down":
            return theme.palette.error.main;
        case "neutral":
            return theme.palette.warning.main;
        default:
            return theme.palette.text.secondary;
    }
};

// Prepare thesis pillars data for table
export const getThesisPillarsColumns = () => [
    {
        id: 1,
        title: "Thesis Pillars",
        sortable: false,
        value: "investment_pillar",
        cell: (data: string) => data,
        show: true,
    },
    {
        id: 2,
        title: "Status",
        sortable: false,
        value: "pillar_status_value",
        cell: (data: string, row: any) => ({
            data,
            status: row.pillar_status,
        }),
        show: true,
    },
    {
        id: 3,
        title: "Evidence from Notes",
        sortable: false,
        value: "reasoning_markdown",
        cell: (data: string) => data,
        show: true,
    },
];

// Prepare BQ voting items
export const getBQVotingItems = (
    votingData: CompanySummary["voting_bq"] & CompanySummary["voting_mq"]
) => [
    {
        key: "moat",
        label: "Moat",
        score: votingData.moat_score,
        comment: votingData.moat_comment_markdown,
        sentiment: votingData.moat_llm_sentiment,
    },
    {
        key: "castle",
        label: "Castle",
        score: votingData.castle_score,
        comment: votingData.castle_comment_markdown,
        sentiment: votingData.castle_llm_sentiment,
    },
    {
        key: "pricing",
        label: "Pricing",
        score: votingData.pricing_score,
        comment: votingData.pricing_comment_markdown,
        sentiment: votingData.pricing_llm_sentiment,
    },
    {
        key: "ecosystem",
        label: "Ecosystem",
        score: votingData.ecosystem_score,
        comment: votingData.ecosystem_comment_markdown,
        sentiment: votingData.ecosystem_llm_sentiment,
    },
    {
        key: "sustainability",
        label: "Sustainability",
        score: votingData.sustainability_score,
        comment: votingData.sustainability_comment_markdown,
        sentiment: votingData.sustainability_llm_sentiment,
    },
    {
        key: "stability",
        label: "Stability",
        score: votingData.stability_score,
        comment: votingData.stability_comment_markdown,
        sentiment: votingData.stability_llm_sentiment,
    },
    {
        key: "difficulty",
        label: "Difficulty",
        score: votingData.difficulty_score,
        comment: votingData.difficulty_comment_markdown,
        sentiment: votingData.difficulty_llm_sentiment,
    },
];

// Prepare MQ voting items
export const getMQVotingItems = (votingData: any) => [
    {
        key: "strategy",
        label: "Strategy",
        score: votingData.strategy_score,
        comment: votingData.strategy_comment_markdown,
        sentiment: votingData.strategy_llm_sentiment,
    },
    {
        key: "operations",
        label: "Operations",
        score: votingData.operations_score,
        comment: votingData.operations_comment_markdown,
        sentiment: votingData.operations_llm_sentiment,
    },
    {
        key: "people",
        label: "People",
        score: votingData.people_score,
        comment: votingData.people_comment_markdown,
        sentiment: votingData.people_llm_sentiment,
    },
    {
        key: "governance",
        label: "Governance",
        score: votingData.governance_score,
        comment: votingData.governance_comment_markdown,
        sentiment: votingData.governance_llm_sentiment,
    },
    {
        key: "culture",
        label: "Culture",
        score: votingData.culture_score,
        comment: votingData.culture_comment_markdown,
        sentiment: votingData.culture_llm_sentiment,
    },
    {
        key: "alignment",
        label: "Alignment",
        score: votingData.alignment_score,
        comment: votingData.alignment_comment_markdown,
        sentiment: votingData.alignment_llm_sentiment,
    },
    {
        key: "capital_allocation",
        label: "Capital Allocation",
        score: votingData.capital_allocation_score,
        comment: votingData.capital_allocation_comment_markdown,
        sentiment: votingData.capital_allocation_llm_sentiment,
    },
    {
        key: "lead_intrinsic_alignment",
        label: "Leadership inc Intr. Alignment",
        score: votingData.lead_intrinsic_alignment_score,
        comment: votingData.lead_intrinsic_alignment_comment_markdown,
        sentiment: votingData.lead_intrinsic_alignment_llm_sentiment,
    },
    {
        key: "gov_extrinsic_alignment",
        label: "Governance and Extr. Alignment",
        score: votingData.gov_extrinsic_alignment_score,
        comment: votingData.gov_extrinsic_alignment_comment_markdown,
        sentiment: votingData.gov_extrinsic_alignment_llm_sentiment,
    },
    {
        key: "people_stakeholders",
        label: "People and Stakeholders",
        score: votingData.people_stakeholders_score,
        comment: votingData.people_stakeholders_comment_markdown,
        sentiment: votingData.people_stakeholders_llm_sentiment,
    },
];

export const getLatestMeetingNote = (
    category: string,
    meetingNotes: MeetingNotesSummary[]
) => {
    return meetingNotes
        .filter(
            (note) =>
                category === ALL_CATEGORIES_VALUE || note.category === category
        )
        .reduce<MeetingNotesSummary | null>((latest, current) => {
            if (!latest) {
                return current;
            }

            const currentDate = new Date(current.meeting_note_date).getTime();
            const latestDate = new Date(latest.meeting_note_date).getTime();

            if (currentDate >= latestDate) {
                return current;
            }

            return latest;
        }, null);
};
